/**
 * @fileoverview Provides utility functions for transforming and manipulating line-based code changes, including converting diff changes to a coalesced format and applying changes selectively to a text string. The module supports tracking different types of text modifications such as insertions, removals, and edits with line number context.
 */

export type CoallescedChange = {
  index: number
  value: string
} & ({ type: 'insert' | 'remove' | 'none' } | { type: 'edit'; priorValue: string })

export type CoallescedChangeWithLineNumber = CoallescedChange & { startLine: number }

export function toCoallescedChange(lineDiff: Diff.Change[]): CoallescedChangeWithLineNumber[] {
  const lineChanges: CoallescedChangeWithLineNumber[] = []
  let count = 0
  let startLine = 0
  for (let i = 0; i < lineDiff.length; i++) {
    const thisChange = lineDiff[i]!
    const nextChange = lineDiff[i + 1]
    if (thisChange.removed && nextChange?.added) {
      lineChanges.push({
        type: 'edit',
        value: nextChange.value,
        priorValue: thisChange.value,
        index: count,
        startLine,
      })
      i++
      startLine += thisChange.count || 0
    } else if (thisChange.removed) {
      lineChanges.push({ type: 'remove', value: thisChange.value, index: count, startLine })
    } else if (thisChange.added) {
      lineChanges.push({ type: 'insert', value: thisChange.value, index: count, startLine })
      startLine += thisChange.count || 0
    } else {
      lineChanges.push({ type: 'none', value: thisChange.value, index: count, startLine })
      startLine += thisChange.count || 0
    }
    count++
  }
  return lineChanges
}

export function changesToStringWithOnlyOneChangeApplied(
  changes: CoallescedChange[],
  toApply: CoallescedChange,
): string {
  let result = ''
  for (let i = 0; i < changes.length; i++) {
    const change = changes[i]!
    if (change.index === toApply.index && change.type !== 'remove') {
      result += toApply.value
    } else if (
      change.type === 'none' ||
      (change.type === 'remove' && change.index !== toApply.index)
    ) {
      result += change.value
    } else if (change.type === 'edit') {
      result += change.priorValue
    } else if (change.type === 'insert') {
      // Special case when computed insert is including a space that should actually be in the original.
      const priorChange = changes[i - 1]
      if (
        priorChange?.type === 'none' &&
        !priorChange.value.endsWith(' ') &&
        change.value.endsWith(' ')
      ) {
        result += ' '
      }
    }
  }
  return result
}
